import _ from 'lodash'
import { AxiosResponse } from 'axios'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { apiCall } from '../../../utils/api'
import { downloadExportViaToken, waitForConfigLoad } from '../../../utils/sagaUtils'
import { Dictionary } from '../../../utils/types'
import { InvestigationUmbracoConfigResponse, getAccountReportsPageSize, getCountryCode } from '../../config'
import { DialogActions } from '../../dialog'
import { getVisibleDialogData } from '../../dialog/selectors'
import { DialogType } from '../../dialog/types'
import { withLoading } from '../../loading'
import { ColumnSorting, TableActions, sortParam } from '../../table'
import { selectSorting } from '../../table/selectors'

import { ImportSearchActions } from './actions'
import { SearchedImportItem, SearchedImportItems, SearchedImportSubItem } from './types'
import { getSearchedImportId } from './selectors'

type SearchItems = {
  items?: SearchItem[]
  isItemWithNoResults: boolean
  isItemWithMoreResults: boolean
  allEnabled: boolean
}

type SearchItem = {
  id: string
  enabled: boolean
  isItemWithNoResults: boolean
  isItemWithMoreResults: boolean
  statusCode: string
  searches: SearchResult[]
  companyName?: string
  address?: string
  city?: string
  postalCode?: string
  state?: string
  country?: string
  businessIdType?: string
  businessId?: string
}

type SearchResult = {
  enabled?: boolean
  companyIdentifier?: string
  companyName?: string
  businessId?: string
  businessIdType?: string
  fullAddress?: string
  city?: string
  postalCode?: string
  state?: string
  country?: string
}

type ImportResponse = {
  data: SearchItems
  meta: {
    totalCount: number
    pageCount: number
    pageNumber: number
  }
}

const mapImportSearchedItems = (
  response: SearchItems,
  investigationAvailableCountries: string[]
): SearchedImportItems => ({
  items: _.map(
    response.items,
    (r: SearchItem): SearchedImportItem => ({
      enabled: r.enabled,
      id: r.id,
      notFound: _.size(r.searches) === 0,
      investigationAvailable: _.includes(investigationAvailableCountries, r.country),
      address: _.size(r.searches) === 1 ? _.head(r.searches)?.fullAddress : undefined,
      businessIdType: _.size(r.searches) === 1 ? _.head(r.searches)?.businessIdType : undefined,
      businessIdValue: _.size(r.searches) === 1 ? _.head(r.searches)?.businessId : undefined,
      city: _.size(r.searches) === 1 ? _.head(r.searches)?.city : undefined,
      companyName: _.size(r.searches) === 1 ? _.head(r.searches)?.companyName : r.companyName,
      country: _.size(r.searches) === 1 ? _.head(r.searches)?.country : r.country,
      moreSearchResults:
        _.size(r.searches) > 1
          ? _.map(
              r.searches,
              (sr: SearchResult): SearchedImportSubItem => ({
                id: r.id,
                selected: sr.enabled,
                companyIdentifier: sr.companyIdentifier,
                address: sr.fullAddress,
                businessIdType: sr.businessIdType,
                businessIdValue: sr.businessId,
                city: sr.city,
                companyName: sr.companyName,
                country: sr.country,
                postalCode: sr.postalCode,
                state: sr.state
              })
            )
          : undefined,
      postalCode: _.size(r.searches) === 1 ? _.head(r.searches)?.postalCode : undefined,
      state: _.size(r.searches) === 1 ? _.head(r.searches)?.state : undefined
    })
  ),
  allEnabled: response.allEnabled,
  isItemWithMoreResults: response.isItemWithMoreResults,
  isItemWithNoResults: response.isItemWithNoResults
})

export function* fetchImports(action: ReturnType<typeof ImportSearchActions.fetchData>) {
  yield call(waitForConfigLoad)
  const { payload } = action

  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'importSearchedItems' })

  const [{ data }, { data: investigationAvailabilityData }]: [
    AxiosResponse<ImportResponse>,
    AxiosResponse<InvestigationUmbracoConfigResponse>
  ] = yield all([
    call(apiCall, {
      url: `/api/import/${payload.importId}/items`,
      method: 'get',
      params: {
        page: payload.page,
        pageSize: payload.pageSize,
        order: sortParam(sorting)
      }
    }),
    call(apiCall, {
      url: '/api/content/InvestigationPageConfig',
      method: 'get',
      umbracoCall: true
    })
  ])

  yield put(
    ImportSearchActions.setData({
      data: mapImportSearchedItems(data.data, investigationAvailabilityData.countries),
      page: data.meta.pageNumber,
      totalPages: data.meta.totalCount
    })
  )

  if (data.meta.pageNumber === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'importSearchedItems',
        pagination: {
          current: data.meta.pageNumber,
          pageSize: payload.pageSize,
          total: data.meta.totalCount
        }
      })
    )
  }
}

export function* reloadOnSort(action: ReturnType<typeof TableActions.setSorting>) {
  if (action.payload.tableKey !== 'importSearchedItems') return

  const importId: string = yield select(getSearchedImportId)
  const pageSize: number = yield select(getAccountReportsPageSize)

  yield put(
    ImportSearchActions.fetchData({
      importId,
      page: 1,
      pageSize
    })
  )
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'importSearchedItems') return

  const importId: string = yield select(getSearchedImportId)
  const { pagination } = payload

  yield put(
    ImportSearchActions.fetchData({
      importId,
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* exportResults({ payload }: ReturnType<typeof ImportSearchActions.exportResults>) {
  yield call(waitForConfigLoad)

  const { name, columns, domains, businessIdTypes } = payload

  const importId: string = yield select(getSearchedImportId)
  const countryCode: string = yield select(getCountryCode)
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'importSearchedItems' })

  try {
    yield call(apiCall, {
      url: `/api/import/${importId}/items/export`,
      method: 'post',
      params: {
        domainCode: countryCode
      },
      data: { name, columns, businessIdTypes, order: sortParam(sorting), validationErrors: [], domains, type: 'search' }
    })
  } catch (e) {
    const visibleDialog: DialogType = yield select(getVisibleDialogData)
    if (visibleDialog === 'ImportValidationExport')
      yield put(
        DialogActions.updateDialogData({
          dialog: 'ImportValidationExport',
          data: {
            isError: true
          }
        })
      )
    else {
      yield put(
        DialogActions.showDialogWithData({
          dialog: 'ImportValidationExport',
          data: {
            isError: true
          }
        })
      )
    }
  }
}

export function* downloadExport({ payload }: ReturnType<typeof ImportSearchActions.downloadExport>) {
  const { exportId } = payload

  yield call(downloadExportViaToken, exportId, 'import')
  return
}

/* istanbul ignore next */
export function* importSearchSagas() {
  yield all([
    takeLatest(getType(ImportSearchActions.fetchData), withLoading('importSearch', fetchImports)),
    takeLatest(getType(TableActions.setSorting), reloadOnSort),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeLatest(getType(ImportSearchActions.exportResults), exportResults),
    takeLatest(getType(ImportSearchActions.downloadExport), downloadExport)
  ])
}
